/**
 * AuthContext
 *
 * The single source of truth for authenticated-user state on the client.
 *
 * Responsibilities:
 *   - Boot: read tokens from tokenStorage, if any, call GET /auth/me to
 *     hydrate the user profile. On failure (invalid token, network),
 *     clear tokens and remain in the "not authenticated" state.
 *   - login / register / logout wrap the corresponding authApi calls
 *     and update local user state.
 *   - Registers itself as the `onUnauthorized` handler on the axios
 *     interceptor so an unrecoverable 401 clears user state and
 *     bounces the app back to /login.
 */

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import {
  loginRequest,
  registerRequest,
  logoutRequest,
  getMeRequest,
  requestOtpRequest,
  verifyOtpRequest,
  resendOtpRequest,
  completeOnboardingRequest,
} from '../api/authApi.js';
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
} from '../auth/tokenStorage.js';
import { setUnauthorizedHandler } from '../auth/authClient.js';
import { clearAllUsers as clearAllHistory } from '../stores/localHistoryStore.js';
import { deregisterPush } from '../platform/notifications.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isBooting, setBooting] = useState(true);

  const handleUnauthorized = useCallback(() => {
    setUser(null);
    clearTokens();
    // Purge cached local data so it can't be attributed to whoever
    // signs in next — see localVehiclesStore / localHistoryStore.
    try { clearAllHistory(); } catch { /* best-effort */ }
    // Best-effort: drop native push listeners so a background-arriving
    // notification for the previous account doesn't wake this app. The
    // backend device-token de-registration is a separate call (see
    // src/platform/notifications.js's block-comment for the contract).
    deregisterPush().catch(() => {});
  }, []);

  // Boot: if a token is present, try to hydrate the current user.
  useEffect(() => {
    setUnauthorizedHandler(handleUnauthorized);
    let cancelled = false;
    (async () => {
      const token = getAccessToken();
      if (!token) { setBooting(false); return; }
      try {
        const { user: me } = await getMeRequest();
        if (!cancelled) setUser(me);
      } catch {
        if (!cancelled) {
          setUser(null);
          clearTokens();
        }
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => { cancelled = true; };
  }, [handleUnauthorized]);

  const login = useCallback(async ({ email, password }) => {
    const data = await loginRequest({ email, password });
    if (data?.user) setUser(data.user);
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    const data = await registerRequest(payload);
    if (data?.user) setUser(data.user);
    return data;
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken();
    await logoutRequest({ refreshToken });
    setUser(null);
    clearTokens();
    // Purge locally-cached vehicles + session history so the next
    // user of this browser starts clean. See stores/*.js.
    try { clearAllHistory(); } catch { /* best-effort */ }
    // Drop native push listeners for the same reason as
    // handleUnauthorized — no cross-account leakage of pushes.
    deregisterPush().catch(() => {});
  }, []);

  // ── Unified passwordless OTP flow ────────────────────────────────
  // Everyone starts at requestOtp. verifyOtp resolves to either an
  // authenticated session or an onboarding token; only the former
  // hydrates the context. Onboarding submits via completeOnboarding,
  // which produces the session for a genuinely new user.
  const requestOtp = useCallback((payload) => requestOtpRequest(payload), []);
  const resendOtp  = useCallback((payload) => resendOtpRequest(payload), []);

  const verifyOtp = useCallback(async (payload) => {
    const data = await verifyOtpRequest(payload);
    if (data?.outcome === 'authenticated' && data?.user) setUser(data.user);
    return data;
  }, []);

  const completeOnboarding = useCallback(async (payload) => {
    const data = await completeOnboardingRequest(payload);
    if (data?.user) setUser(data.user);
    return data;
  }, []);

  // Consumer-side helper so pages don't reach into `user` for a bool.
  const value = useMemo(() => ({
    user,
    isAuthenticated: !!user,
    isBooting,
    // Legacy password auth (kept for programmatic callers).
    login,
    register,
    // Unified passwordless OTP + onboarding.
    requestOtp,
    verifyOtp,
    resendOtp,
    completeOnboarding,
    // Session management.
    logout,
    setUser,
  }), [
    user, isBooting, login, register, logout,
    requestOtp, verifyOtp, resendOtp, completeOnboarding,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Colocation of the AuthProvider + useAuth hook is the standard React
// context pattern. The react-refresh rule warns about mixed exports;
// splitting them adds noise without runtime value.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** The auth context, or null outside <AuthProvider> (screens also rendered without one). */
// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalAuth() {
  return useContext(AuthContext);
}
